import baseline from '../data/costPlanningBaseline.json';

export const COST_PLANNING_BASELINE = baseline;

// This model always starts from the dated, pre-change reference. It never
// subtracts savings from a provider forecast or from the fetched snapshot.
export function buildPlanningScenario(mau, overrides = {}, reference = baseline) {
  const a = { ...reference.defaultAssumptions, ...overrides };
  const p = reference.rates;
  const invalid = Object.values(a).some((value) => !Number.isFinite(value) || value < 0)
    || !Number.isFinite(mau) || mau <= 0 || a.days <= 0 || a.days > 31
    || a.dauRatio > 1 || a.speechParticipation > 1 || a.dmParticipation > 1
    || a.foregroundMinutes > 1440 || a.bastionHours > a.days * 24
    || a.budget > 100000 || a.speechRecipients > 10000 || a.dmPerDay > 1000;
  if (invalid) return null;
  const dau = mau * a.dauRatio;
  const foreground = dau * a.foregroundMinutes * a.days;
  const sessions = dau * a.sessionsPerDay * a.days;
  const lambda = p.lambdaPerMillion + 0.25 * 0.2 * p.lambdaGbSecond * 1e6;
  const publisher = p.lambdaPerMillion + 0.5 * 0.5 * p.lambdaGbSecond * 1e6;
  const api = (calls) => calls / 1e6 * (p.httpPerMillion + lambda);
  // Assumed logs, internet egress, and 2% uncached secret reads (two per call).
  const overhead = (calls, bytes) => calls * 1000 / 1e9 * p.logGb
    + bytes / 1e9 * p.egressGb + calls * 0.02 * 2 / 1e6 * p.secretPerMillion;
  const meta = foreground / 5 + sessions;
  const touch = foreground / 5 + sessions;
  const location = foreground / 5 + sessions + foreground * 0.1;
  const coreApi = meta + touch + location + sessions * 12 + dau * a.days * 10 + foreground * 0.1;
  const auth = sessions * 2;
  const assets = meta * a.mapKilobytes * 1000 + sessions * 0.05 * Math.min(mau, 10000) * 50;
  const core = api(coreApi) + auth / 1e6 * lambda
    + sessions * 2 / 1e6 * p.eventPerMillion + foreground / 1e6 * p.connectionMinutePerMillion
    + meta * 4 / 1e6 * p.s3GetPerMillion + (touch + location) * 2 / 1e6 * p.dynamoWritePerMillion
    + overhead(coreApi + auth, assets + coreApi * 1000);
  const speech = dau * a.days * a.speechParticipation * a.speechPerDay;
  const deliveries = speech * a.speechRecipients;
  const speechApi = speech * 1.75 + deliveries * 0.1;
  const speechCost = api(speechApi) + speech / 1e6 * publisher
    + deliveries * 2 / 1e6 * p.eventPerMillion + speech * 2 / 1e6 * p.secretPerMillion
    + speech * 2 / 1e6 * p.s3GetPerMillion
    + overhead(speechApi + speech, deliveries * 1000 + speechApi * 1000)
    + speech * 2000 / 1e9 * p.databaseGbMonth;
  const dm = dau * a.days * a.dmParticipation * a.dmPerDay;
  const dmApi = dm * 1.4 + sessions;
  const worker = dm * 1.5;
  const dmCost = api(dmApi) + (worker + sessions) / 1e6 * lambda
    + (dm * 4 + sessions * 2) / 1e6 * p.eventPerMillion + dm / 1e6 * p.pushPerMillion
    + overhead(dmApi + worker + sessions, dm * 3000 + dmApi * 1000)
    + dm * 2000 * 3 / 1e9 * (p.databaseGbMonth + p.backupGbMonth);
  const recurringSavings = reference.savings.reduce((sum, item) => sum
    + (item.monthlyNet || 0) * a.days / reference.referenceDays, 0);
  const bastionRate = reference.savings.find((item) => item.component === 'bastion').hourlyNet;
  const bastionSavings = (a.days * 24 - a.bastionHours) * bastionRate;
  const alarmAdjustment = (reference.currentAlarmMonthlyCost - reference.referenceAlarmMonthlyCost)
    * a.days / reference.referenceDays;
  const operatingAdjustment = reference.additionalBaseAllowance + alarmAdjustment - recurringSavings - bastionSavings;
  const parts = [
    { key: 'operating', low: reference.dailyOperatingCost.low * a.days + operatingAdjustment, high: reference.dailyOperatingCost.high * a.days + operatingAdjustment },
    { key: 'core', low: core + (coreApi + auth) * 2 / 1e6 * p.dbIoPerMillion, high: core + (coreApi + auth) * 20 / 1e6 * p.dbIoPerMillion },
    { key: 'speech', low: speechCost + (speechApi + speech) * 5 / 1e6 * p.dbIoPerMillion, high: speechCost + (speechApi + speech) * 30 / 1e6 * p.dbIoPerMillion },
    { key: 'dm', low: dmCost + (dmApi + worker) * 10 / 1e6 * p.dbIoPerMillion, high: dmCost + (dmApi + worker) * 40 / 1e6 * p.dbIoPerMillion },
    { key: 'reserve', low: a.mediaReserve + a.operationsReserve, high: a.mediaReserve + a.operationsReserve },
  ];
  const low = parts.reduce((sum, part) => sum + part.low, 0);
  const high = parts.reduce((sum, part) => sum + part.high, 0);
  const calls = coreApi + auth + speechApi + dmApi;
  return { mau, low, high, parts, dau, speech, dm, calls, averageRps: calls / (a.days * 86400),
    recurringSavings, bastionSavings, alarmAdjustment, headroom: a.budget - high,
    budget: a.budget, days: a.days, reserve: a.mediaReserve + a.operationsReserve };
}
